import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import portfolioService, { ChatTurn, ChatEvent, streamPortfolioQuestion } from '../../services/portfolio';
import { useCurrency } from '../../contexts/CurrencyContext';
import ScenarioDiffChart, { ScenarioDiffData } from './ScenarioDiffChart';
import PerformanceChart, { PerformanceData } from './PerformanceChart';
import EtfHoldingsChart, { EtfHoldingsData } from './EtfHoldingsChart';
import WealthProjectionChart, { WealthProjectionData } from './WealthProjectionChart';
import RiskSection from './RiskSection';
import ValuationSection from './ValuationSection';
import InfoTooltip from '../ui/InfoTooltip';
import MemoryPanel from './MemoryPanel';
import ConversationsPanel from './ConversationsPanel';

// What the assistant can currently do — shown in the header info box.
const ASSISTANT_INFO = (
  <div>
    <div style={{ fontWeight: 600, color: '#000537', marginBottom: 6 }}>What Aumora can do</div>
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      <li>Explain your exposure, concentration, sectors &amp; geography</li>
      <li>Blended valuation (P/E, P/B, yield) and risk (volatility, beta, Sharpe, drawdown, VaR)</li>
      <li>Look up holdings, a stock or ETF&apos;s performance, or an ETF&apos;s top holdings</li>
      <li>Run what-if scenarios — buy / sell / resize / replace positions</li>
      <li>Simulate market shocks — a stock, sector, or ETF moving</li>
      <li>Project wealth — Monte Carlo paths and the odds of reaching a goal</li>
    </ul>
    <div style={{ marginTop: 6, color: '#71717a' }}>
      Grounded in your computed data and shown as charts — clarity, not advice.
    </div>
  </div>
);

const NewChatIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MemoryIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const ConversationsIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const headerBtn: React.CSSProperties = {
  fontSize: '0.72rem', color: '#71717a', cursor: 'pointer', lineHeight: 1,
};

interface Props {
  portfolioId: number;
}

// Grouped example prompts for the landing state.
const PROMPT_GROUPS: { label: string; prompts: string[] }[] = [
  {
    label: 'Understand',
    prompts: ['What is my biggest exposure?', 'How concentrated am I?', 'What is my geographic breakdown?'],
  },
  {
    label: 'Simulate',
    prompts: ['What if I sell Apple and buy a world ETF?', 'How would a 15% tech drop hit me?'],
  },
  {
    label: 'Plan',
    prompts: ['Could I reach €1M by 2045 saving €1,000/mo?', 'What could this be worth in 15 years?'],
  },
  {
    label: 'Explore',
    prompts: ['How has my biggest holding performed?', "What's inside my largest ETF?"],
  },
];

const PromptChip: React.FC<{ text: string; onClick: () => void }> = ({ text, onClick }) => (
  <button type="button" onClick={onClick} className="chat-prompt">{text}</button>
);

// Tool-call progress phrases: present continuous while running, past tense once
// done (e.g. "Getting market data…" -> "Fetched market data").
const TOOL_PHRASES: Record<string, { running: string; done: string }> = {
  get_blended_valuation: { running: 'Computing valuation', done: 'Computed valuation' },
  get_risk_metrics: { running: 'Computing risk metrics', done: 'Computed risk metrics' },
  list_holdings: { running: 'Looking up holdings', done: 'Looked up holdings' },
  simulate_position_changes: { running: 'Running the scenario', done: 'Ran the scenario' },
  simulate_market_shock: { running: 'Simulating the market shock', done: 'Simulated the market shock' },
  get_performance: { running: 'Getting market data', done: 'Fetched market data' },
  get_etf_holdings: { running: 'Fetching ETF holdings', done: 'Fetched ETF holdings' },
  project_wealth: { running: 'Projecting wealth', done: 'Projected wealth' },
  remember_about_household: { running: 'Updating memory', done: 'Updated memory' },
};

// Incoming (assistant) messages: plain navy text, no bubble/outline — it sits on
// the panel like Anthropic/Gemini. Full width of the centered conversation column.
const assistantTextBubble: React.CSSProperties = {
  width: '100%', fontSize: '0.9rem', lineHeight: 1.65, color: '#1a1f3d', marginBottom: 8,
};

// A live tool-call item: present-tense + spinner while running, past tense once done.
type LiveItem =
  | { type: 'tool_call'; id: string; name: string; running: boolean }
  | { type: 'artifact'; kind: string; data: unknown }
  | { type: 'text'; content: string };

// Subtle progress line — italic light grey, no chip/box.
const ToolChip: React.FC<{ name: string; running?: boolean }> = ({ name, running }) => {
  const phrases = TOOL_PHRASES[name] ?? { running: name, done: name };
  return (
    <div className="chat-rise" style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      fontSize: '0.78rem', fontStyle: 'italic', color: '#a1a1aa', marginBottom: 6,
    }}>
      {running && <span className="chat-spinner" />}
      {running ? `${phrases.running}…` : phrases.done}
    </div>
  );
};

// Animated "Aumora is thinking" placeholder before the first event lands.
const ThinkingDots: React.FC = () => (
  <div className="chat-rise d-flex align-items-center" style={{ gap: 8, color: '#a1a1aa', fontSize: '0.82rem', fontStyle: 'italic', padding: '2px 2px 6px' }}>
    Aumora is thinking
    <span className="chat-dots"><span /><span /><span /></span>
  </div>
);

const markdownBubble = (content: string, key: number) => (
  <div key={key} className="chat-md chat-rise" style={assistantTextBubble}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
  </div>
);

// Render a tool artifact. scenario_diff/performance/etf_holdings are driven by the
// artifact data; risk/valuation reuse the existing section components (fetch by id).
// Wrapped in .chat-artifact so embedded card-forest sections match the chart cards.
const artifactBlock = (kind: string, data: unknown, key: number, portfolioId: number) => {
  let body: React.ReactNode = null;
  if (kind === 'scenario_diff') body = <ScenarioDiffChart data={data as ScenarioDiffData} />;
  else if (kind === 'performance') body = <PerformanceChart data={data as PerformanceData} />;
  else if (kind === 'etf_holdings') body = <EtfHoldingsChart data={data as EtfHoldingsData} />;
  else if (kind === 'wealth_projection') body = <WealthProjectionChart data={data as WealthProjectionData} />;
  else if (kind === 'risk') body = <RiskSection portfolioId={portfolioId} />;
  else if (kind === 'valuation') body = <ValuationSection portfolioId={portfolioId} />;
  if (!body) return null;
  return <div key={key} className="chat-artifact chat-rise" style={{ width: '100%', marginBottom: 10 }}>{body}</div>;
};

// Committed-turn event (chips always shown as done).
function renderEvent(ev: ChatEvent, key: number, portfolioId: number) {
  if (ev.type === 'tool_call') return <ToolChip key={key} name={ev.name} />;
  if (ev.type === 'text') return markdownBubble(ev.content, key);
  if (ev.type === 'artifact') return artifactBlock(ev.kind, ev.data, key, portfolioId);
  return null;
}

// In-progress live item (tool chips reflect running state).
function renderLiveItem(it: LiveItem, key: number, portfolioId: number) {
  if (it.type === 'tool_call') return <ToolChip key={key} name={it.name} running={it.running} />;
  if (it.type === 'text') return markdownBubble(it.content, key);
  if (it.type === 'artifact') return artifactBlock(it.kind, it.data, key, portfolioId);
  return null;
}

const toChatEvent = (it: LiveItem): ChatEvent => {
  if (it.type === 'tool_call') return { type: 'tool_call', name: it.name };
  if (it.type === 'artifact') return { type: 'artifact', kind: it.kind, data: it.data };
  return { type: 'text', content: it.content };
};

const lastText = (items: LiveItem[]): string => {
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    if (it.type === 'text') return it.content;
  }
  return '';
};

// Conversational surface over the portfolio (ROADMAP.md M3). The server streams a
// transcript of events (tool calls, charts, text); the AI orchestrates and the
// engine computes — answers are grounded in the snapshot and tool results.
const ChatSection: React.FC<Props> = ({ portfolioId }) => {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [live, setLive] = useState<LiveItem[] | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [conversationsOpen, setConversationsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [height, setHeight] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef<LiveItem[]>([]);
  const { displayCurrency } = useCurrency();

  // Fill the viewport below wherever the chat starts, so the input pins to the
  // bottom and the transcript scrolls above it — measured (not hard-coded) so it
  // survives changes to the page header/tabs above. While mounted we lock page
  // scroll: the page is exactly full-screen and never scrollable, only the
  // transcript scrolls.
  useLayoutEffect(() => {
    const update = () => {
      if (rootRef.current) {
        const top = rootRef.current.getBoundingClientRect().top;
        setHeight(Math.max(360, window.innerHeight - top - 12));
      }
    };
    update();
    window.addEventListener('resize', update);

    const html = document.documentElement;
    const prevHtml = html.style.overflow;
    const prevBody = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('resize', update);
      html.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  const setLiveItems = (next: LiveItem[]) => { liveRef.current = next; setLive(next); };

  const finish = () => { liveRef.current = []; setLive(null); setIsStreaming(false); };

  const send = useCallback(async (question: string) => {
    const q = question.trim();
    if (!q || isStreaming) return;

    const history = turns; // committed turns before this question
    setTurns((prev) => [...prev, { role: 'user', content: q }]);
    setInput('');
    setError(null);
    setLiveItems([]);
    setIsStreaming(true);

    // Persistence is best-effort: chat still works if it fails. Create the
    // conversation on the first turn (titled from the question).
    let convId = conversationId;
    if (convId == null) {
      try {
        const conv = await portfolioService.createConversation(portfolioId, q.slice(0, 60));
        convId = conv.id;
        setConversationId(convId);
      } catch { /* ignore */ }
    }

    try {
      await streamPortfolioQuestion(portfolioId, q, history, displayCurrency, (ev) => {
        switch (ev.type) {
          case 'tool_call':
            setLiveItems([...liveRef.current, { type: 'tool_call', id: ev.id, name: ev.name, running: true }]);
            break;
          case 'tool_result':
            setLiveItems(liveRef.current.map((it) =>
              it.type === 'tool_call' && it.id === ev.id ? { ...it, running: false } : it));
            break;
          case 'artifact':
            setLiveItems([...liveRef.current, { type: 'artifact', kind: ev.kind, data: ev.data }]);
            break;
          case 'text':
            setLiveItems([...liveRef.current, { type: 'text', content: ev.content }]);
            break;
          case 'done': {
            const events = liveRef.current.map(toChatEvent);
            const content = ev.answer || lastText(liveRef.current);
            setTurns((prev) => [...prev, { role: 'assistant', content, events }]);
            finish();
            if (convId != null) {
              portfolioService.appendMessages(portfolioId, convId, [
                { role: 'user', content: q },
                { role: 'assistant', content, events },
              ]).catch(() => { /* best-effort */ });
            }
            break;
          }
          case 'error':
            setError(ev.message || 'The AI service is unavailable right now.');
            finish();
            break;
        }
      });
    } catch {
      setError('The AI service is unavailable right now. Please try again.');
      finish();
    }
  }, [isStreaming, turns, portfolioId, displayCurrency, conversationId]);

  // Load a saved conversation and resume it.
  const loadConversation = useCallback(async (id: number) => {
    if (isStreaming) return;
    try {
      const conv = await portfolioService.getConversation(portfolioId, id);
      setTurns(conv.messages.map((m) => ({
        role: m.role,
        content: m.content,
        events: m.role === 'assistant' ? m.events : undefined,
      })));
      setConversationId(id);
      setLive(null);
      setError(null);
    } catch {
      setError('Could not load that conversation.');
    }
  }, [isStreaming, portfolioId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, live]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  // Start fresh: drop the transcript (and the AI's in-context history with it).
  const reset = () => {
    if (isStreaming) return;
    setTurns([]);
    setInput('');
    setError(null);
    setLive(null);
    setConversationId(null);
  };

  const hasConversation = turns.length > 0 || isStreaming || error !== null;

  return (
    <div
      ref={rootRef}
      className="d-flex flex-column"
      // Pull up into the page's top padding so the input can sit higher off the
      // bottom without the page scrolling (height is measured after this margin).
      style={{ height: height ? `${height}px` : '75vh', marginTop: '-20px' }}
    >
      {/* Header — full width: title at the left edge, actions at the right edge. */}
      <div className="d-flex justify-content-between align-items-center" style={{ paddingBottom: '14px' }}>
        <span className="aumora-serif" style={{ fontSize: '1.1rem', fontWeight: 500, color: '#000537' }}>Ask Aumora</span>
        <div className="d-flex align-items-center gap-3" style={{ lineHeight: 1 }}>
          <button
            type="button"
            onClick={() => setConversationsOpen(true)}
            className="border-0 bg-transparent p-0 d-inline-flex align-items-center gap-1"
            style={headerBtn}
          >
            <ConversationsIcon />
            Conversations
          </button>
          <button
            type="button"
            onClick={() => setMemoryOpen(true)}
            className="border-0 bg-transparent p-0 d-inline-flex align-items-center gap-1"
            style={headerBtn}
          >
            <MemoryIcon />
            Memory
          </button>
          {hasConversation && (
            <button
              type="button"
              onClick={reset}
              disabled={isStreaming}
              className="border-0 bg-transparent p-0 d-inline-flex align-items-center gap-1"
              style={{ fontSize: '0.72rem', color: '#71717a', cursor: 'pointer', lineHeight: 1 }}
            >
              <NewChatIcon />
              New chat
            </button>
          )}
          <span className="d-inline-flex align-items-center">
            <InfoTooltip text={ASSISTANT_INFO} width={320} placement="bottom" />
          </span>
        </div>
      </div>

      {/* Transcript (scrolls; minHeight:0 lets it shrink inside the flex column) */}
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '4px' }}>
        <div className="chat-col">
          {turns.length === 0 && !isStreaming && (
            <div className="chat-rise" style={{ maxWidth: 640, margin: '0 auto', paddingTop: 'min(8vh, 64px)' }}>
              <div className="aumora-serif" style={{ fontSize: '1.7rem', fontWeight: 500, color: '#000537', textAlign: 'center' }}>
                Ask Aumora
              </div>
              <p style={{ color: '#71717a', fontSize: '0.9rem', lineHeight: 1.65, margin: '12px auto 28px', textAlign: 'center', maxWidth: 520 }}>
                Your portfolio, explained. Ask about exposure and risk, explore a holding, or run a
                what-if — every answer is grounded in your computed data and shown as charts.
                Clarity, not advice.
              </p>
              <div className="d-flex flex-column" style={{ gap: 18 }}>
                {PROMPT_GROUPS.map((g) => (
                  <div key={g.label}>
                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#a1a1aa', marginBottom: 9 }}>
                      {g.label}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {g.prompts.map((s) => <PromptChip key={s} text={s} onClick={() => send(s)} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn, i) =>
            turn.role === 'user' ? (
              <div key={i} className="d-flex chat-rise" style={{ justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={{
                  maxWidth: '80%', fontSize: '0.875rem', lineHeight: 1.55, padding: '10px 15px',
                  borderRadius: '16px 16px 4px 16px', whiteSpace: 'pre-wrap', background: '#000537', color: '#fff',
                }}>
                  {turn.content}
                </div>
              </div>
            ) : (
              <div key={i} className="d-flex flex-column align-items-start" style={{ width: '100%', marginBottom: '20px' }}>
                {turn.events && turn.events.length > 0
                  ? turn.events.map((ev, j) => renderEvent(ev, j, portfolioId))
                  : markdownBubble(turn.content, 0)}
              </div>
            )
          )}

          {/* In-progress turn (streaming live) */}
          {live !== null && (
            <div className="d-flex flex-column align-items-start" style={{ width: '100%', marginBottom: '20px' }}>
              {live.length === 0 ? <ThinkingDots /> : live.map((it, j) => renderLiveItem(it, j, portfolioId))}
            </div>
          )}

          {error && (
            <div style={{ fontSize: '0.8rem', color: '#b91c1c', marginBottom: '10px' }}>{error}</div>
          )}
        </div>
      </div>

      {/* Composer pinned near the bottom (with breathing room below it) */}
      <form onSubmit={onSubmit} className="d-flex gap-2 chat-col" style={{ paddingTop: '12px', paddingBottom: '28px' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your portfolio…"
          className="form-control"
          style={{ fontSize: '0.875rem', borderColor: '#e4e4e7' }}
          disabled={isStreaming}
        />
        <button
          type="submit"
          className="btn"
          disabled={isStreaming || !input.trim()}
          style={{ background: '#000537', color: '#fff', fontSize: '0.85rem', padding: '6px 18px', whiteSpace: 'nowrap' }}
        >
          Send
        </button>
      </form>

      {memoryOpen && <MemoryPanel portfolioId={portfolioId} onClose={() => setMemoryOpen(false)} />}
      {conversationsOpen && (
        <ConversationsPanel
          portfolioId={portfolioId}
          currentId={conversationId}
          onSelect={loadConversation}
          onNew={reset}
          onDeleted={reset}
          onClose={() => setConversationsOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatSection;
